/**
 * SEO Head Component
 * Manages dynamic meta tags for each page
 */

import { Helmet } from 'react-helmet-async';
import {
  generateMetaTitle,
  generateMetaDescription,
  generateMetaKeywords,
  generateCanonicalUrl,
  generateStructuredData,
  generateBreadcrumbData,
  defaultMeta,
  type GlobalSeoTemplate,
} from '@/lib/seo';
import { useSiteSettings } from '@/hooks/useSiteSettings';

// Reads the admin's Global Settings tab (SEOManagePage) — real site_settings rows
// (category='seo', is_public=1), not the localStorage-only values that tab used
// to be limited to. Empty fields resolve to undefined so callers' `?.` checks
// correctly fall through to hardcoded per-category copy.
function useGlobalSeoSettings() {
  const { getValue } = useSiteSettings();
  const template: GlobalSeoTemplate = {
    titleTemplate: getValue('seo_title_template') || undefined,
    descriptionTemplate: getValue('seo_description_template') || undefined,
    defaultKeywords: getValue('seo_default_keywords') || undefined,
  };
  const disabledSchemaTypesRaw = getValue('seo_disabled_schema_types');
  const disabledSchemaTypes = disabledSchemaTypesRaw
    ? disabledSchemaTypesRaw.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n))
    : [];
  return {
    template,
    ogDefaultImage: getValue('seo_og_default_image', defaultMeta.ogImage),
    twitterCard: getValue('seo_twitter_card', 'summary_large_image'),
    disabledSchemaTypes,
  };
}

const SITE_URL = `https://${import.meta.env.VITE_MEDIA_DOMAIN || 'www.patakare.com'}`;

// App-wide baseline meta tags, always mounted at the root (see App.tsx). Static
// equivalents used to live directly in index.html, but a plain HTML <meta> tag
// isn't Helmet-managed — a page-specific <Helmet> (e.g. ListingSEO) can't replace
// it, so both ended up in the DOM at once with the generic static one winning
// (first in document order). Routing the default through Helmet too lets
// per-page Helmet instances correctly override it via Helmet's own de-dup.
export const DefaultSEO = () => {
  const { ogDefaultImage, twitterCard } = useGlobalSeoSettings();
  return (
    <Helmet>
      <title>{defaultMeta.title}</title>
      <meta name="description" content={defaultMeta.description} />
      <meta name="keywords" content={defaultMeta.keywords} />
      <meta name="author" content="Patakare" />
      <meta property="og:title" content={defaultMeta.title} />
      <meta property="og:description" content={defaultMeta.description} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={ogDefaultImage} />
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:site" content={defaultMeta.twitterHandle} />
      <meta name="twitter:image" content={ogDefaultImage} />
    </Helmet>
  );
};

interface ListingSEOProps {
  listing: {
    id: number;
    name: string;
    categoryType: number;
    description?: string;
    address?: string;
    city?: string;
    state?: string;
    phone?: string;
    email?: string;
    website?: string;
    image?: string;
    images?: string[];
    rating?: number;
    reviewCount?: number;
    specialization?: string;
    qualification?: string;
    services?: string[];
    timing?: Record<string, { open: string; close: string; isOpen: boolean }>;
    // Health Camp (categoryType 9) only — the event's own date/time and
    // organizer, needed to build a schema.org Event with a real startDate.
    // Optional everywhere else.
    eventDate?: string;
    startTime?: string;
    endTime?: string;
    organizerName?: string;
    isFreeEvent?: boolean;
    // Admin-set overrides from the per-listing SEO Settings dialog — take
    // priority over the auto-generated values below when present.
    metaTitleOverride?: string;
    metaDescriptionOverride?: string;
    metaKeywordsOverride?: string;
    canonicalUrlOverride?: string;
    noIndex?: boolean;
  };
}

// SEO component for listing detail pages
export const ListingSEO = ({ listing }: ListingSEOProps) => {
  const { template: globalTemplate, ogDefaultImage, twitterCard, disabledSchemaTypes } = useGlobalSeoSettings();

  const title = listing.metaTitleOverride || generateMetaTitle(
    listing.name,
    listing.categoryType,
    listing.city,
    listing.specialization,
    globalTemplate
  );

  // .replace(/\s+/g, ' ').trim() as a safety net on the FINAL value (not just
  // inside generateMetaDescription()'s own fallback branch) — an admin-saved
  // override can carry the exact same raw \r\n/whitespace mess (confirmed
  // live: the admin SEO editor's "auto-generate" button persists
  // generateMetaDescription()'s output straight into meta_description, so a
  // listing saved before that function was fixed still has the dirty value
  // sitting in the DB as an "override" that bypasses the generator entirely).
  const description = (listing.metaDescriptionOverride || generateMetaDescription(
    listing.name,
    listing.categoryType,
    listing.city,
    listing.state,
    listing.specialization,
    listing.description,
    globalTemplate
  )).replace(/\s+/g, ' ').trim();

  const keywords = (listing.metaKeywordsOverride || generateMetaKeywords(
    listing.name,
    listing.categoryType,
    listing.city,
    listing.state,
    listing.specialization,
    listing.services,
    globalTemplate
  )).replace(/\s+/g, ' ').trim();

  const canonicalUrl = listing.canonicalUrlOverride || generateCanonicalUrl(
    listing.id,
    listing.name,
    listing.categoryType,
    listing.city,
    listing.state
  );

  const structuredData = generateStructuredData(listing, disabledSchemaTypes);
  const breadcrumbData = generateBreadcrumbData(listing);

  const ogImage = listing.image || listing.images?.[0] || ogDefaultImage;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="Patakare" />
      <meta name="robots" content={listing.noIndex ? 'noindex, nofollow' : 'index, follow'} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={listing.categoryType === 9 ? 'event' : 'website'} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Patakare" />
      <meta property="og:locale" content="en_IN" />
      
      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:site" content={defaultMeta.twitterHandle} />

      {/* Additional SEO */}
      <meta name="geo.region" content="IN" />
      {listing.city && <meta name="geo.placename" content={listing.city} />}

      {/* Structured Data (JSON-LD) — structuredData is null when the admin has
          disabled this category's schema in the Schema Engine tab */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbData)}
      </script>
    </Helmet>
  );
};

// SEO for listings page (category listing)
interface ListingsPageSEOProps {
  category?: string;
  categoryType?: number;
  city?: string;
  state?: string;
  totalCount?: number;
  // Set by ListingsV2 when a clean /doctors/gwalior(/cosmetologist) URL exists
  // for this exact result set — takes priority over the ?type=&city= form so
  // Google is only ever told one canonical URL for the same listings.
  canonicalPath?: string;
  // True when a specific city+category combination has too few real listings
  // to be worth indexing — programmatic SEO's classic trap is generating one
  // page per city×category combination regardless of how many (if any) real
  // listings exist there; Google treats a site with lots of near-empty pages
  // as low-quality, which can drag down pages that DO have real content. This
  // keeps the page itself working normally for visitors, just out of the index
  // until enough listings exist.
  noIndex?: boolean;
}

export const ListingsPageSEO = ({
  category,
  categoryType,
  city,
  state,
  totalCount,
  canonicalPath,
  noIndex,
}: ListingsPageSEOProps) => {
  const { ogDefaultImage, twitterCard } = useGlobalSeoSettings();
  const location = city || state || 'India';
  const categoryName = category || 'Healthcare Providers';
  // "Health Camp" (singular label, matches ListingsV2's TYPE_META) reads like
  // one specific event when used as a directory title/description — every
  // other category's label is already a natural plural (Doctors, Hospitals),
  // this is the one exception. Only pluralize this exact known singular
  // rather than guessing at English pluralization rules for any future label.
  const categoryNamePlural = categoryName === 'Health Camp' ? 'Health Camps' : categoryName;
  // "appointment booking" doesn't describe what you actually do on a Health
  // Camp/Blood Bank/Ambulance listing — was the same generic phrase for every
  // category regardless of its real call-to-action, confirmed live 2026-08-20.
  const ctaPhraseByType: Partial<Record<number, string>> = {
    9: 'registration',
    8: 'blood requests',
    10: 'ambulance booking',
  };
  const ctaPhrase = (categoryType && ctaPhraseByType[categoryType]) || 'appointment booking';

  let title = '';
  let description = '';

  if (categoryType && city) {
    title = `Best ${categoryNamePlural} in ${city} | Patakare`;
    description = `Find top ${categoryNamePlural.toLowerCase()} in ${city}${state ? `, ${state}` : ''}. ${totalCount ? `${totalCount}+ verified ` : ''}${categoryNamePlural.toLowerCase()} with reviews, contact details & ${ctaPhrase} on Patakare.`;
  } else if (categoryType) {
    title = `${categoryNamePlural} Directory | Find ${categoryNamePlural} Near You | Patakare`;
    description = `Discover ${totalCount ? `${totalCount}+ ` : ''}trusted ${categoryNamePlural.toLowerCase()} across India. Search by location, specialization & reviews. ${ctaPhrase.charAt(0).toUpperCase() + ctaPhrase.slice(1)} on Patakare.`;
  } else if (city) {
    title = `Healthcare Services in ${city} | Patakare`;
    description = `Find hospitals, doctors, pharmacies & healthcare services in ${city}${state ? `, ${state}` : ''}. ${totalCount ? `${totalCount}+ ` : ''}verified healthcare providers on Patakare.`;
  } else {
    title = 'Healthcare Directory | Find Hospitals, Doctors & Medical Services | Patakare';
    description = `India's trusted healthcare directory. Find ${totalCount ? `${totalCount}+ ` : ''}hospitals, doctors, pharmacies, diagnostic centers & ambulance services near you.`;
  }
  
  // Keywords
  const keywords = [
    categoryName.toLowerCase(),
    `${categoryName.toLowerCase()} near me`,
    location.toLowerCase(),
    `healthcare ${location.toLowerCase()}`,
    'patakare',
    'healthcare directory',
    'find doctor',
    'hospital near me',
    'medical services',
  ].join(', ');
  
  const canonicalUrl = canonicalPath
    ? `${SITE_URL}${canonicalPath}`
    : `${SITE_URL}/listings${categoryType ? `?type=${categoryType}` : ''}${city ? `&city=${encodeURIComponent(city)}` : ''}`;
  
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content={noIndex ? 'noindex, follow' : 'index, follow'} />
      <link rel="canonical" href={canonicalUrl} />
      
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogDefaultImage} />

      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogDefaultImage} />
    </Helmet>
  );
};

// SEO for home page
export const HomeSEO = () => {
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Patakare',
    url: SITE_URL,
    // Was a single-quoted string containing literal "${SITE_URL}" text — template-literal
    // syntax only interpolates inside backticks, so this schema's logo field has always
    // been the broken literal string, never the real URL. Also pointed at /favicon.jpg,
    // which was never a real file (confirmed live: returns the SPA's HTML shell, not an
    // image) — the actual favicon is /favicon-patakare-v2.png (confirmed 200, image/png).
    logo: `${SITE_URL}/favicon-patakare-v2.png`,
    description: defaultMeta.description,
    // facebook.com/Patakare was never the real page — confirmed via live Google search,
    // the actual verified page (32K+ followers) is facebook.com/patakarecom.
    sameAs: [
      'https://twitter.com/Patakare',
      'https://www.facebook.com/patakarecom',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: ['English', 'Hindi'],
    },
  };
  
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Patakare',
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        // Same single-quoted-literal bug as the logo field above — this never actually
        // pointed at the real domain, so Google's Sitelinks Search Box could never have
        // worked off this schema.
        urlTemplate: `${SITE_URL}/listings?search={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
  
  return (
    <Helmet>
      <title>{defaultMeta.title}</title>
      <meta name="title" content={defaultMeta.title} />
      <meta name="description" content={defaultMeta.description} />
      <meta name="keywords" content={defaultMeta.keywords} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={SITE_URL} />
      
      <meta property="og:type" content="website" />
      <meta property="og:url" content={SITE_URL} />
      <meta property="og:title" content={defaultMeta.title} />
      <meta property="og:description" content={defaultMeta.description} />
      <meta property="og:image" content={defaultMeta.ogImage} />
      
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={defaultMeta.title} />
      <meta name="twitter:description" content={defaultMeta.description} />
      <meta name="twitter:image" content={defaultMeta.ogImage} />
      
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
    </Helmet>
  );
};

// Strips HTML tags and collapses whitespace to build a real meta description
// straight from a page's actual admin-edited content, instead of a hardcoded
// string that could silently drift out of sync with what the page displays.
// Mirrored in bridgeapi/seo-render.php's seoRenderStaticPage() for the
// crawler pre-render — same source (the editable_pages content), same
// truncation length, so the two never show Google conflicting descriptions
// for the same URL.
export function htmlToMetaDescription(html: string, maxLength = 160): string {
  const text = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 1).replace(/\s+\S*$/, '') + '…';
}

// SEO for the six admin-editable static pages (About/Contact/Terms/Privacy/
// Refund/Cookies) — these previously had ZERO page-specific meta tags at
// all, even client-side, so every one of them showed the exact same generic
// site-wide title/description (confirmed live: identical browser tab titles
// across all six). title/description now come straight from the same
// useEditablePages() content already rendered on the page.
interface StaticPageSEOProps {
  title: string;
  htmlContent: string;
  path: string;
}

export const StaticPageSEO = ({ title, htmlContent, path }: StaticPageSEOProps) => {
  const fullTitle = `${title} | Patakare`;
  const description = htmlToMetaDescription(htmlContent) || defaultMeta.description;
  const canonicalUrl = `${SITE_URL}${path}`;
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
    </Helmet>
  );
};

// SEO for auth page
export const AuthSEO = () => (
  <Helmet>
    <title>Login / Register | Patakare Healthcare Directory</title>
    <meta name="description" content="Login or register on Patakare to list your healthcare business, manage your profile, and connect with patients across India." />
    <meta name="robots" content="noindex, nofollow" />
  </Helmet>
);

// SEO for Blood Bank pages
interface BloodBankSEOProps {
  bank?: {
    id: string;
    name: string;
    city?: string;
    state?: string;
    address?: string;
    phone?: string;
    logo_url?: string;
  };
  isSearch?: boolean;
  city?: string;
  bloodGroup?: string;
}

export const BloodBankSEO = ({ bank, isSearch, city, bloodGroup }: BloodBankSEOProps) => {
  if (isSearch) {
    const locationStr = city ? ` in ${city}` : '';
    const bgStr = bloodGroup && bloodGroup !== 'all' ? ` ${bloodGroup}` : '';
    const title = `Blood Banks${locationStr} | Find${bgStr} Blood Stock | Patakare`;
    const description = `Search blood banks${locationStr} with real-time${bgStr} blood stock availability. Emergency blood donation, blood bank directory on Patakare.`;
    const canonicalUrl = `${SITE_URL}/blood-banks${city ? `?city=${encodeURIComponent(city)}` : ''}`;
    
    return (
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={`blood bank${city ? ` ${city}` : ''}, blood donation, blood stock, ${bloodGroup || 'all blood groups'}, patakare`} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={defaultMeta.ogImage} />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>
    );
  }

  if (bank) {
    const location = [bank.city, bank.state].filter(Boolean).join(', ') || 'India';
    const title = `${bank.name} | Blood Bank in ${location} | Patakare`;
    const description = `${bank.name} - Blood bank in ${location}. Check real-time blood stock, donate blood, emergency requests. Contact: ${bank.phone || 'Available on Patakare'}`;
    const canonicalUrl = `${SITE_URL}/blood-banks?bank=${bank.id}`;
    
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'MedicalOrganization',
      name: bank.name,
      url: canonicalUrl,
      image: bank.logo_url || defaultMeta.ogImage,
      address: {
        '@type': 'PostalAddress',
        streetAddress: bank.address || '',
        addressLocality: bank.city || '',
        addressRegion: bank.state || '',
        addressCountry: 'IN',
      },
      telephone: bank.phone,
      medicalSpecialty: 'Blood Bank',
    };
    
    return (
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={bank.logo_url || defaultMeta.ogImage} />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>
    );
  }

  return null;
};

// SEO for Ambulance pages
interface AmbulanceSEOProps {
  provider?: {
    id: string;
    provider_name: string;
    city?: string;
    state?: string;
    ambulance_type?: string;
    contact_number?: string;
    photo_url?: string;
  };
  isSearch?: boolean;
  city?: string;
  ambulanceType?: string;
}

export const AmbulanceSEO = ({ provider, isSearch, city, ambulanceType }: AmbulanceSEOProps) => {
  if (isSearch) {
    const locationStr = city ? ` in ${city}` : '';
    const typeStr = ambulanceType && ambulanceType !== 'all' ? ` ${ambulanceType.toUpperCase()}` : '';
    const title = `${typeStr} Ambulance Services${locationStr} | 24/7 Emergency | Patakare`;
    const description = `Book${typeStr} ambulance${locationStr}. ICU, Ventilator, Cardiac, Air Ambulance available 24/7. Emergency & scheduled booking on Patakare.`;
    const canonicalUrl = `${SITE_URL}/ambulance${city ? `?city=${encodeURIComponent(city)}` : ''}`;
    
    return (
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={`ambulance${city ? ` ${city}` : ''}, emergency ambulance, ${ambulanceType || 'icu ambulance'}, 24/7, patakare`} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={defaultMeta.ogImage} />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>
    );
  }

  if (provider) {
    const location = [provider.city, provider.state].filter(Boolean).join(', ') || 'India';
    const title = `${provider.provider_name} | Ambulance Service in ${location} | Patakare`;
    const description = `${provider.provider_name} - ${provider.ambulance_type || 'Ambulance'} service in ${location}. 24/7 emergency booking. Contact: ${provider.contact_number || 'Available on Patakare'}`;
    const canonicalUrl = `${SITE_URL}/ambulance?provider=${provider.id}`;
    
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'EmergencyService',
      name: provider.provider_name,
      url: canonicalUrl,
      image: provider.photo_url || defaultMeta.ogImage,
      address: {
        '@type': 'PostalAddress',
        addressLocality: provider.city || '',
        addressRegion: provider.state || '',
        addressCountry: 'IN',
      },
      telephone: provider.contact_number,
      serviceType: 'Ambulance',
    };
    
    return (
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={provider.photo_url || defaultMeta.ogImage} />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>
    );
  }

  return null;
};

// SEO for HomeCare+ pages
interface HomeCareSEOProps {
  provider?: {
    id: string;
    full_name: string;
    city?: string;
    state?: string;
    role_types?: string[];
    mobile?: string;
    photo_url?: string;
    total_experience_years?: number;
  };
  isSearch?: boolean;
  city?: string;
  roleType?: string;
}

export const HomeCareSEO = ({ provider, isSearch, city, roleType }: HomeCareSEOProps) => {
  const roleLabels: Record<string, string> = {
    care_taker: 'Care Taker',
    home_nurse: 'Home Nurse',
    elderly_care: 'Elderly Care',
    child_care: 'Child Care',
  };

  if (isSearch) {
    const locationStr = city ? ` in ${city}` : '';
    const typeStr = roleType && roleType !== 'all' ? ` ${roleLabels[roleType] || roleType}` : '';
    const title = `HomeCare+ |${typeStr} Services${locationStr} | Patakare`;
    const description = `Find verified${typeStr} professionals${locationStr}. Home nurse, care taker, elderly care with KYC verification. Book on Patakare.`;
    const canonicalUrl = `${SITE_URL}/homecare${city ? `?city=${encodeURIComponent(city)}` : ''}`;
    
    return (
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={`home care${city ? ` ${city}` : ''}, ${roleType || 'home nurse'}, care taker, elderly care, patakare`} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={defaultMeta.ogImage} />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>
    );
  }

  if (provider) {
    const location = [provider.city, provider.state].filter(Boolean).join(', ') || 'India';
    const roles = provider.role_types?.map(r => roleLabels[r] || r).join(', ') || 'Care Professional';
    const title = `${provider.full_name} | ${roles} in ${location} | Patakare`;
    const description = `${provider.full_name} - KYC verified ${roles} in ${location}${provider.total_experience_years ? `. ${provider.total_experience_years}+ years experience` : ''}. Hire on Patakare.`;
    const canonicalUrl = `${SITE_URL}/homecare?provider=${provider.id}`;
    
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: provider.full_name,
      url: canonicalUrl,
      image: provider.photo_url || defaultMeta.ogImage,
      jobTitle: roles,
      address: {
        '@type': 'PostalAddress',
        addressLocality: provider.city || '',
        addressRegion: provider.state || '',
        addressCountry: 'IN',
      },
      telephone: provider.mobile,
    };
    
    return (
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={provider.photo_url || defaultMeta.ogImage} />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>
    );
  }

  return null;
};

// SEO for dashboard pages (no-index)
export const DashboardSEO = ({ title }: { title: string }) => (
  <Helmet>
    <title>{title} | Dashboard | Patakare</title>
    <meta name="robots" content="noindex, nofollow" />
  </Helmet>
);

export default {
  DefaultSEO,
  ListingSEO,
  ListingsPageSEO,
  HomeSEO,
  AuthSEO,
  DashboardSEO,
  BloodBankSEO,
  AmbulanceSEO,
  HomeCareSEO,
};
